import { useEffect, useRef, useState } from 'react';
import Editor, { loader, type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { monaco } from '@/lib/monaco';

// Point @monaco-editor/react at the locally-bundled monaco instance (workers
// are bundled via `lib/monaco.ts` — no CDN).
loader.config({ monaco });

interface CodeEditorProps {
  language?: string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  markers?: editor.IMarkerData[];
  height?: string | number;
}

/**
 * Track the app theme (`.dark` class on <html>) so Monaco follows it.
 * Uses a MutationObserver as the app has no theme provider.
 */
function useMonacoTheme(): 'vs-dark' | 'vs' {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false,
  );

  useEffect(() => {
    const update = () =>
      setIsDark(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return isDark ? 'vs-dark' : 'vs';
}

export function CodeEditor({
  language = 'javascript',
  value,
  onChange,
  readOnly = false,
  markers,
  height = 320,
}: CodeEditorProps) {
  const theme = useMonacoTheme();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleMount: OnMount = (ed) => {
    editorRef.current = ed;
    if (markers && markers.length > 0) {
      const model = ed.getModel();
      if (model) monaco.editor.setModelMarkers(model, 'webhook-validation', markers);
    }
  };

  // Apply validation markers (and clear stale ones) whenever they change.
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const model = ed.getModel();
    if (!model) return;
    monaco.editor.setModelMarkers(model, 'webhook-validation', markers ?? []);
    // Let the editor re-layout once wrapped (e.g. initial marker mount).
    ed.layout();
  }, [markers]);

  return (
    <Editor
      language={language}
      theme={theme}
      value={value}
      height={height}
      onMount={handleMount}
      onChange={(next) => onChange?.(next ?? '')}
      options={{
        readOnly,
        minimap: { enabled: false },
        fontSize: 13,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        wordWrap: 'on',
        renderValidationDecorations: 'on',
      }}
    />
  );
}